// Translation hook for easy usage in components
import { useCallback, useMemo, useEffect } from "react";
import useStore from "@/store/store";
import { setDayjsLocale } from "@/lib/dayjsConfig";
import { resolveTranslationValue } from "@/lib/translationUtils";

/**
 * Hook for accessing translations in components
 * @returns {Object} Translation utilities and state
 */
export const useTranslation = () => {
  const store = useStore();

  const {
    translations = {},
    currentLanguage = "en",
    availableLanguages = ["en", "mk", "sq", "tr"],
    isLoadingTranslations = false,
    translationError = null,
    setCurrentLanguage,
    loadTranslations,
    getTranslation,
    hasTranslation,
    refreshTranslations,
    initializeTranslations,
  } = store;

  // Sync dayjs locale with current language
  useEffect(() => {
    setDayjsLocale(currentLanguage);
  }, [currentLanguage]);
  /**
   * Get translation by key path with optional interpolation
   * @param {string} key - Translation key path (e.g., 'header.TitleDashboard')
   * @param {string} defaultValue - Default value if translation not found
   * @param {Object} interpolation - Values to interpolate in the translation
   * @returns {string} Translated text
   */
  const t = useCallback(
    (key, defaultValue = key, interpolation = {}) => {
      // If getTranslation function is available, use it
      if (getTranslation && typeof getTranslation === "function") {
        let translation = getTranslation(key, defaultValue);

        // Handle interpolation
        if (Object.keys(interpolation).length > 0) {
          Object.entries(interpolation).forEach(([placeholder, value]) => {
            const regex = new RegExp(`{{\\s*${placeholder}\\s*}}`, "g");
            translation = translation.replace(regex, value);
          });
        }

        return translation;
      }

      // Fallback: try to get translation directly from store
      const langTranslations = translations[currentLanguage];
      if (!langTranslations) {
        return defaultValue;
      }

      let translation = resolveTranslationValue(langTranslations, key);

      if (typeof translation !== "string") {
        translation = defaultValue;
      }

      // Handle interpolation
      if (Object.keys(interpolation).length > 0) {
        Object.entries(interpolation).forEach(([placeholder, value]) => {
          const regex = new RegExp(`{{\\s*${placeholder}\\s*}}`, "g");
          translation = translation.replace(regex, value);
        });
      }

      return translation;
    },
    [getTranslation, translations, currentLanguage]
  );
  /**
   * Get translation with pluralization support
   * @param {string} key - Translation key path
   * @param {number} count - Count for pluralization
   * @param {string} defaultValue - Default value if translation not found
   * @param {Object} interpolation - Values to interpolate
   * @returns {string} Translated text
   */
  const tc = useCallback(
    (key, count, defaultValue = key, interpolation = {}) => {
      let pluralKey = key;

      // Try to find plural form based on count
      if (count === 0) {
        pluralKey = `${key}.zero`;
      } else if (count === 1) {
        pluralKey = `${key}.one`;
      } else {
        pluralKey = `${key}.many`;
      }

      // Check if plural form exists
      const checkTranslationExists = (checkKey) => {
        if (hasTranslation && typeof hasTranslation === "function") {
          return hasTranslation(checkKey);
        }

        // Fallback check
        const langTranslations = translations[currentLanguage];
        if (!langTranslations) return false;

        const keys = checkKey.split(".");
        let value = langTranslations;

        for (const keyPart of keys) {
          if (value && typeof value === "object" && keyPart in value) {
            value = value[keyPart];
          } else {
            return false;
          }
        }

        return typeof value === "string";
      };

      // Fallback to base key if plural form doesn't exist
      if (!checkTranslationExists(pluralKey)) {
        pluralKey = key;
      }

      return t(pluralKey, defaultValue, { count, ...interpolation });
    },
    [t, hasTranslation, translations, currentLanguage]
  );
  /**
   * Change language
   * @param {string} language - Language code to switch to
   * @returns {Promise} Promise that resolves when language is changed
   */
  const changeLanguage = useCallback(
    async (language) => {
      if (setCurrentLanguage && typeof setCurrentLanguage === "function") {
        if (availableLanguages.includes(language)) {
          await setCurrentLanguage(language);
        } else {
          console.warn(
            `Language '${language}' is not available. Available languages:`,
            availableLanguages
          );
        }
      } else {
        console.warn("setCurrentLanguage function is not available yet");
      }
    },
    [setCurrentLanguage, availableLanguages]
  );

  /**
   * Get current translations for the active language
   */
  const currentTranslations = useMemo(() => {
    return translations[currentLanguage] || {};
  }, [translations, currentLanguage]);

  /**
   * Check if translations are loaded for current language
   */
  const isReady = useMemo(() => {
    return (
      !isLoadingTranslations &&
      currentTranslations &&
      Object.keys(currentTranslations).length > 0
    );
  }, [isLoadingTranslations, currentTranslations]);

  /**
   * Get language display name
   * @param {string} langCode - Language code
   * @returns {string} Display name for the language
   */
  const getLanguageDisplayName = useCallback((langCode) => {
    const languageNames = {
      en: "English",
      mk: "Македонски",
      sq: "Shqip",
      tr: "Türkçe",
      // Add more language display names as needed
    };

    return languageNames[langCode] || langCode.toUpperCase();
  }, []);
  return {
    // Translation functions
    t,
    tc, // Translation with count/pluralization

    // State
    currentLanguage,
    availableLanguages,
    isLoadingTranslations,
    translationError,
    isReady,
    currentTranslations,

    // Actions (with safe fallbacks)
    changeLanguage,
    refreshTranslations: refreshTranslations || (() => Promise.resolve()),
    initializeTranslations: initializeTranslations || (() => Promise.resolve()),

    // Utilities
    hasTranslation: hasTranslation || (() => false),
    getLanguageDisplayName,
  };
};

export default useTranslation;
